import { useState } from "react";
import { PaperPlaneTilt, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { money } from "@/lib/money";

// ONE consolidated payment-reminder email per client covering every open invoice
// at/over `minAgeDays` (by week ending). Manual only — nothing is ever sent automatically.
export default function ClientReminderModal({ client, invoices, minAgeDays = 90, onClose, onSent }) {
  const [to, setTo] = useState(client.billing_email || "");
  const [cc, setCc] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const total = invoices.reduce((s, i) => s + Number(i.balance_due ?? i.balance ?? 0), 0);
  const changed = to.trim() && to.trim() !== (client.billing_email || "").trim();
  const inp = "mt-1 w-full h-11 px-3 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-rose-400";

  const submit = async (e) => {
    e.preventDefault();
    if (!to.trim()) { toast.error("Enter a recipient email."); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/clients/${client.client_id || client.id}/remind-overdue`, { min_age_days: minAgeDays, to: to.trim(), cc: cc.trim() || null, note: note.trim() || null });
      toast.success(`Reminder sent to ${data.to.join(", ")} — ${data.invoices} invoice${data.invoices === 1 ? "" : "s"}, ${money(data.total)}.`);
      onSent?.(data);
    } catch (e2) { toast.error(e2.response?.data?.detail?.toString() || e2.message, { duration: 8000 }); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 flex items-center justify-center p-4 z-[60]" data-testid="client-reminder-modal" onClick={() => !busy && onClose()}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white rounded-md w-full max-w-lg p-6 border border-slate-200 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-xl font-bold flex items-center gap-2"><PaperPlaneTilt size={22} weight="duotone" className="text-rose-600" /> Payment reminder — {minAgeDays}+ days</h2>
          <button type="button" onClick={onClose} className="text-slate-500 hover:text-slate-800"><X size={20} /></button>
        </div>
        <p className="text-sm text-slate-600">One email to <b>{client.client_name || client.name}</b> listing <b>{invoices.length} invoice{invoices.length === 1 ? "" : "s"}</b> totalling <b className="font-mono">{money(total)}</b>, each with a link to its PDF.</p>
        <ul className="mt-2 max-h-28 overflow-y-auto text-xs text-slate-600 border border-slate-200 rounded-md divide-y divide-slate-100" data-testid="client-reminder-list">
          {invoices.map((i) => (
            <li key={i.id} className="flex justify-between px-3 py-1.5"><span className="font-mono">{i.number}</span><span>{i.age_days != null ? `${i.age_days}d` : ""}</span><span className="font-mono font-semibold">{money(i.balance_due ?? i.balance)}</span></li>
          ))}
        </ul>

        <label className="block mt-4 text-xs font-bold uppercase tracking-widest text-slate-500">To
          <input type="text" value={to} onChange={(e) => setTo(e.target.value)} data-testid="client-reminder-to" placeholder="payables@client.com" className={inp} />
        </label>
        <div className="text-[11px] mt-1 text-slate-500">
          {client.billing_email ? (changed ? <span className="text-amber-700 font-semibold">Different from the billing email on file ({client.billing_email}) — use this when someone else asked for it.</span> : "Defaults to the email invoices are sent to. Change it if the request came from someone else.") : <span className="text-amber-700 font-semibold">No billing email on file — enter one.</span>}
        </div>
        <label className="block mt-3 text-xs font-bold uppercase tracking-widest text-slate-500">CC (optional)
          <input type="text" value={cc} onChange={(e) => setCc(e.target.value)} data-testid="client-reminder-cc" placeholder="comma-separated" className={inp} />
        </label>
        <label className="block mt-3 text-xs font-bold uppercase tracking-widest text-slate-500">Personal note (optional)
          <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} data-testid="client-reminder-note" placeholder="e.g. Per our call today — thanks for looking into these." className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md" />
        </label>
        <p className="text-xs text-slate-400 mt-3">The office is BCC'd. Each invoice records this reminder date. Nothing is ever sent automatically.</p>
        <div className="mt-5 flex gap-3 justify-end">
          <button type="button" onClick={onClose} disabled={busy} className="h-11 px-4 border border-slate-300 rounded-md font-semibold">Cancel</button>
          <button data-testid="client-reminder-send" disabled={busy || !to.trim()} className="h-11 px-4 bg-rose-600 hover:bg-rose-700 text-white font-semibold rounded-md disabled:opacity-60 inline-flex items-center gap-2">
            <PaperPlaneTilt size={16} weight="bold" /> {busy ? "Sending…" : "Send reminder"}
          </button>
        </div>
      </form>
    </div>
  );
}
